import {action, computed, makeObservable, observable} from "mobx";

type MenuState =
    | "rolled-up-menu"
    | "rolled-down-menu"
    | "collapsed-menu"
    | "full-screen-menu";

const COLLAPSED_MENU_BREAKPOINT = 1280;
const ROLLED_UP_MENU: MenuState = "rolled-up-menu";
const ROLLED_DOWN_MENU: MenuState = "rolled-down-menu";
const COLLAPSED_MENU: MenuState = "collapsed-menu";
const FULL_SCREEN_MENU: MenuState = "full-screen-menu";

export class MenuStore {
    @observable
    private _state: MenuState;

    @observable
    private _openCloseState: boolean;

    constructor() {
        this._openCloseState = localStorage.getItem("openCloseState") === "1";
        this._state = this.calculateState(window.innerWidth);

        makeObservable(this);
    }

    @computed
    public get openCloseState(): boolean {
        return this._openCloseState && this._state === ROLLED_UP_MENU;
    }

    @computed
    public get fullScreenModeState(): boolean {
        return this._state === FULL_SCREEN_MENU;
    }

    @computed
    public get isRolledDown(): boolean {
        return this._state === ROLLED_DOWN_MENU;
    }

    @computed
    public get getStateForMenu(): string {
        return this._state;
    }

    @computed
    public get getStateForContainer(): string {
        return this._state;
    }

    @action
    public toggleOpenCloseState(): void {
        if (this._state === COLLAPSED_MENU || this._state === FULL_SCREEN_MENU) {
            return;
        }

        this._openCloseState = !this._openCloseState;
        localStorage.setItem("openCloseState", this._openCloseState ? "1" : "0");
        this.setMenuState(this._openCloseState ? ROLLED_UP_MENU : ROLLED_DOWN_MENU);
    }

    @action
    public setViewportWidth(width: number): void {
        if (this._state === FULL_SCREEN_MENU && width <= COLLAPSED_MENU_BREAKPOINT) {
            return;
        }

        this.setMenuState(this.calculateState(width));
    }

    @action
    public toggleFullScreenModeState(): void {
        if (this._state === COLLAPSED_MENU) {
            this.setMenuState(FULL_SCREEN_MENU);
        } else if (this._state === FULL_SCREEN_MENU) {
            this.setMenuState(COLLAPSED_MENU);
        }
    }

    @action
    public closeFullScreenMode(): void {
        if (this._state === FULL_SCREEN_MENU) {
            this.setMenuState(COLLAPSED_MENU);
        }
    }

    private calculateState(width: number): MenuState {
        if (width <= COLLAPSED_MENU_BREAKPOINT) {
            return COLLAPSED_MENU;
        }

        return this._openCloseState ? ROLLED_UP_MENU : ROLLED_DOWN_MENU;
    }

    private setMenuState(state: MenuState): void {
        this._state = state;
    }
}
